import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useElementSize } from "usehooks-ts";
import Widget from "../../baseWidget";
import { useWidgetContext } from "../../../helpers/hooks/useWidgetContext";
import LevelNavigation from "./levelNavigation";
import StackedBarChartWithDrillDown from "./stackedBarChartWithDrillDown";
import styled from "styled-components";
import ValueSelectDropdown from "../../valueSelectDropdown";

// Types for hierarchical widget configuration
export interface HierarchicalConfig {
  current_level: string;
  level_params: Record<string, any>;
  levels_meta: LevelMeta[];
  can_drill_down: boolean;
  drill_down_config: DrillDownConfig | null;
  parent_dropdown: ParentDropdownConfig | null;
  breadcrumb: BreadcrumbItem[];
}

export interface LevelMeta {
  name: string;
  has_parent: boolean;
  parent_param: string | null;
  can_drill_down: boolean;
}

export interface DrillDownConfig {
  target_level: string;
  param_key: string;
}

export interface ParentDropdownConfig {
  enabled: boolean;
  param_key: string;
  current_value: string | number;
  current_label: string;
}

export interface BreadcrumbItem {
  level: string;
  label: string;
  params: Record<string, any>;
}

// Parent items for dropdown (cached from root level)
export interface ParentItem {
  id: string | number;
  name: string;
}

const HierarchicalWidget = () => {
  const [widgetRef, contentDimensions] = useElementSize();
  const widget = useWidgetContext();

  // Track navigation state to prevent double-clicks
  const [isNavigating, setIsNavigating] = useState(false);

  // Cache parent level items for dropdown
  const [parentItemsCache, setParentItemsCache] = useState<ParentItem[]>([]);

  // SWR-style cache: store root level data for instant back navigation
  const rootLevelCacheRef = useRef<any>(null);

  // Selected dimension for the dropdown (Sales, Orders, etc.)
  const [selectedDimension, setSelectedDimension] = useState<{ key: string; name: string } | null>(null);

  // Extract hierarchical config from widget data
  const hierarchicalConfig: HierarchicalConfig | null = useMemo(() => {
    return widget?.widgetData?.hierarchical_config || null;
  }, [widget?.widgetData]);

  // Reset navigation state when data changes
  useEffect(() => {
    if (hierarchicalConfig) {
      setIsNavigating(false);
    }
  }, [hierarchicalConfig]);

  // Get visualization type from widget data
  const visualizationType = useMemo(() => {
    return widget?.widgetData?.type || null;
  }, [widget?.widgetData]);

  // Get visualization data (the actual chart data)
  const visualizationData = useMemo(() => {
    if (!widget?.widgetData) return null;
    // For bar.stacked_bar.v1, data structure is: { type, title, config, data }
    return widget.widgetData;
  }, [widget?.widgetData]);

  // Check if we have data
  const hasData = useMemo(() => {
    const data = visualizationData?.data;
    return Array.isArray(data) && data.length > 0;
  }, [visualizationData]);

  // Get dimension options from value_configs
  const dimensionOptions = useMemo(() => {
    if (!visualizationData?.config?.visual?.value_configs) return [];
    return visualizationData.config.visual.value_configs.map((v: any) => ({
      key: v.accessor,
      name: v.name,
    }));
  }, [visualizationData]);

  // Set default dimension when options change
  useEffect(() => {
    if (dimensionOptions.length > 0 && !selectedDimension) {
      setSelectedDimension(dimensionOptions[0]);
    }
  }, [dimensionOptions, selectedDimension]);

  // Cache root level data for SWR-style back navigation
  useEffect(() => {
    if (
      hierarchicalConfig &&
      hierarchicalConfig.current_level === hierarchicalConfig.levels_meta?.[0]?.name && // At root level
      visualizationData
    ) {
      rootLevelCacheRef.current = {
        data: visualizationData,
        timestamp: Date.now(),
      };

      // Also cache parent items for dropdown
      const drillConfig = hierarchicalConfig.drill_down_config;
      if (drillConfig && Array.isArray(visualizationData.data)) {
        const categoryAccessor = visualizationData.config?.visual?.category_config?.accessor;
        const idAccessor = visualizationData.config?.visual?.category_config?.id_accessor;

        const items = visualizationData.data.map((item: any) => ({
          id: item[idAccessor] || item[drillConfig.param_key] || item.id,
          name: item[categoryAccessor] || item[drillConfig.param_key.replace("_id", "")] || item.name || String(item.id),
        }));
        setParentItemsCache(items);
      }
    }
  }, [hierarchicalConfig, visualizationData]);

  // Handle drill-down when user clicks on a data point
  const handleDrillDown = useCallback(
    (dataPoint: any) => {
      if (!hierarchicalConfig?.can_drill_down) return;
      if (isNavigating || widget.widgetLoading) return;

      setIsNavigating(true);

      // Use embedded __drill_down params if present
      if (dataPoint?.__drill_down) {
        const { level, ...params } = dataPoint.__drill_down;
        const filterParams = [
          { key: "level", value: String(level) },
          ...Object.entries(params).map(([key, value]) => ({
            key,
            value: String(value),
          })),
        ];
        widget.getWidgetDataWithFilters?.(filterParams);
        return;
      }

      // Fallback: extract from drill_down_config
      if (!hierarchicalConfig.drill_down_config) return;

      const { target_level, param_key } = hierarchicalConfig.drill_down_config;
      const idAccessor = visualizationData?.config?.visual?.category_config?.id_accessor || param_key;
      const nameAccessor = visualizationData?.config?.visual?.category_config?.accessor;

      const paramValue = dataPoint[idAccessor] || dataPoint[param_key] || dataPoint.id;
      const paramLabel = dataPoint[nameAccessor] || dataPoint[param_key.replace("_id", "")] || dataPoint.name || String(paramValue);

      if (!paramValue && paramValue !== 0) return;

      widget.getWidgetDataWithFilters?.([
        { key: "level", value: String(target_level) },
        { key: param_key, value: String(paramValue) },
        { key: `${param_key.replace("_id", "_name")}`, value: String(paramLabel) },
      ]);
    },
    [hierarchicalConfig, widget, isNavigating, visualizationData]
  );

  // Handle navigation back to root - SWR style: show cached data immediately, then refresh
  const handleNavigateToRoot = useCallback(() => {
    if (isNavigating || widget.widgetLoading) return;

    setIsNavigating(true);

    // If we have cached root data, we could show it immediately here
    // For now, just refresh to get fresh data
    widget.refreshData?.();
  }, [widget, isNavigating]);

  // Handle parent dropdown selection
  const handleParentSelect = useCallback(
    (selectedItem: ParentItem) => {
      if (!hierarchicalConfig?.parent_dropdown) return;

      const { param_key } = hierarchicalConfig.parent_dropdown;

      widget.getWidgetDataWithFilters?.([
        { key: "level", value: String(hierarchicalConfig.current_level) },
        { key: param_key, value: String(selectedItem.id) },
        { key: `${param_key.replace("_id", "_name")}`, value: String(selectedItem.name) },
      ]);
    },
    [hierarchicalConfig, widget]
  );

  // Handle dimension dropdown selection
  const handleDimensionSelect = useCallback((option: { key: string; name: string }) => {
    setSelectedDimension(option);
  }, []);

  // Check if we're in a child level (not root)
  const isChildLevel = useMemo(() => {
    return hierarchicalConfig?.breadcrumb && hierarchicalConfig.breadcrumb.length > 1;
  }, [hierarchicalConfig]);

  // Widget title
  const widgetTitle = useMemo(() => {
    if (isChildLevel && hierarchicalConfig?.breadcrumb?.[1]?.label) {
      return hierarchicalConfig.breadcrumb[1].label;
    }
    return visualizationData?.title || "Hierarchical Widget";
  }, [visualizationData, hierarchicalConfig, isChildLevel]);

  // Render the appropriate visualization based on type
  const renderVisualization = () => {
    if (!visualizationData || !hasData) {
      return (
        <Widget.EmptyData
          width={contentDimensions.width - 50}
          height={contentDimensions.height - 95}
        />
      );
    }

    // For bar.stacked_bar.v1, use our custom clickable chart
    if (visualizationType === "bar.stacked_bar.v1") {
      return (
        <StackedBarChartWithDrillDown
          data={visualizationData}
          width={contentDimensions.width - 50}
          height={contentDimensions.height - 95}
          selectedDimension={selectedDimension}
          onDataPointClick={hierarchicalConfig?.can_drill_down ? handleDrillDown : undefined}
        />
      );
    }

    // Fallback for unknown types
    return (
      <Widget.EmptyData
        width={contentDimensions.width - 50}
        height={contentDimensions.height - 95}
      />
    );
  };

  return (
    <Widget ref={widgetRef}>
      <Widget.Header>
        <Widget.Title>{widgetTitle}</Widget.Title>
        <Widget.Controls>
          <HeaderControls>
            {/* Back button when in child level */}
            {isChildLevel && hierarchicalConfig && (
              <LevelNavigation
                config={hierarchicalConfig}
                parentItems={parentItemsCache}
                onNavigateToRoot={handleNavigateToRoot}
                onParentSelect={handleParentSelect}
                isChildLevel={isChildLevel}
              />
            )}

            {/* Extended A Report CTA */}
            {hasData && widget.enableExtendedAReport && <Widget.ExtendedAReportCta />}

            {/* Dimension dropdown (Sales, Orders, etc.) */}
            {hasData && dimensionOptions.length > 1 && selectedDimension && (
              <ValueSelectDropdown
                options={dimensionOptions}
                onChange={handleDimensionSelect}
                selectedOption={selectedDimension}
              />
            )}
          </HeaderControls>
        </Widget.Controls>
      </Widget.Header>

      <Widget.Content>
        <VisualizationWrapper $isClickable={hierarchicalConfig?.can_drill_down}>
          {renderVisualization()}

          {/* Drill-down hint */}
          {hierarchicalConfig?.can_drill_down && hasData && (
            <DrillDownHint>
              <HintIcon>🔍</HintIcon>
              <span>Click on the bars to explore</span>
            </DrillDownHint>
          )}
        </VisualizationWrapper>
      </Widget.Content>
    </Widget>
  );
};

const VisualizationWrapper = styled.div<{ $isClickable?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;

  ${({ $isClickable }) =>
    $isClickable &&
    `
    /* Add subtle indicator that chart is interactive */
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 40px;
      pointer-events: none;
      border-radius: 8px;
      transition: box-shadow 0.2s ease;
    }
  `}
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;

  @media (max-width: 767px) {
    width: 100%;
    justify-content: flex-start;

    .dropdown {
      max-width: 100%;
    }

    .dropdown-toggle {
      max-width: 100%;
      font-size: 13px;
      padding: 4px 10px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

const DrillDownHint = styled.div`
  position: absolute;
  bottom: 45px; /* Above x-axis labels */
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  color: var(--primary-color, #0f80d1);
  background: var(--widget-bg, rgba(255, 255, 255, 0.95));
  padding: 5px 12px;
  border-radius: 14px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--primary-color-light, #e0f0ff);
  pointer-events: none;
  z-index: 10;
  white-space: nowrap;
`;

const HintIcon = styled.span`
  font-size: 12px;
  line-height: 1;
`;

export default HierarchicalWidget;
